import React from 'react';
import { Modal, Button, Form } from 'react-bootstrap';

/**
 * FormModal - Standardized modal structure for forms
 * Provides consistent modal UI with header, body, and footer
 *
 * @param {boolean} show - Whether modal is visible
 * @param {Function} onHide - Handler to close modal
 * @param {string} title - Modal title
 * @param {ReactNode} children - Form content
 * @param {Function} onSubmit - Form submit handler
 * @param {string} submitText - Submit button text (default: 'Save')
 * @param {string} cancelText - Cancel button text (default: 'Cancel')
 * @param {string} submitVariant - Submit button variant (default: 'primary')
 * @param {boolean} disableSubmit - Whether submit is disabled
 * @param {string} size - Modal size ('sm', 'lg', 'xl')
 * @param {ReactNode} footer - Custom footer content (overrides default buttons)
 *
 * @example
 * <FormModal
 *   show={showModal}
 *   onHide={() => setShowModal(false)}
 *   title="Add New Customer"
 *   onSubmit={handleSubmit}
 *   submitText="Add Customer"
 * >
 *   <Form.Group>
 *     <Form.Label>Name</Form.Label>
 *     <Form.Control type="text" value={name} onChange={e => setName(e.target.value)} />
 *   </Form.Group>
 * </FormModal>
 */
export const FormModal = ({
  show,
  onHide,
  title,
  children,
  onSubmit,
  submitText = 'Save',
  cancelText = 'Cancel',
  submitVariant = 'primary',
  disableSubmit = false,
  size,
  footer,
}) => {
  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSubmit) {
      onSubmit(e);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size={size} centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {children}
        </Modal.Body>

        <Modal.Footer>
          {footer || (
            <>
              <Button variant="secondary" onClick={onHide}>
                {cancelText}
              </Button>
              <Button
                variant={submitVariant}
                type="submit"
                disabled={disableSubmit}
              >
                {submitText}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default FormModal;
